import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FrontFirstTreeActionDto,
  FrontFirstTreeGroupDto,
  FrontFirstTreeSubgroupDto,
  frontFirstGroupIcon,
} from './front-first-permission-tree.types';

// Faixa unicode de diacríticos combinantes (0x0300-0x036f), construída via
// RegExp a partir de \u escapes (nunca caracteres literais no arquivo-fonte)
// — mesmo padrão de `front-first-group-tree.builder.ts` (abcx-api) e
// `system-settings-search-items.builder.ts` (abcx-web). Portado do abcx-web (staging 70989884)
// para o wazir-web — a árvore vem de `GET permission-groups/front-first-tree` (wazir-api).
const DIACRITICS_RANGE_START = 0x0300;
const DIACRITICS_RANGE_END = 0x036f;
const DIACRITICS_REGEX = new RegExp(
  `[\\u${DIACRITICS_RANGE_START.toString(16).padStart(4, '0')}-\\u${DIACRITICS_RANGE_END.toString(16).padStart(4, '0')}]`,
  'g'
);

/**
 * Seletor de permissões "front-first" — árvore GRUPO (área do menu) →
 * SUBGRUPO (tela) → AÇÃO (front `MENU_*`/botão + back API), fiel ao
 * protótipo aprovado (artifact a6b93afd-6703-4da3-9565-cdce5e486bab — ver
 * specs/permissions/front-first-permission-groups.md §10, Fase B).
 *
 * Diferença de propósito vs. `app-permission-selector` (legado,
 * controller-driven): aqui a árvore já chega PRONTA do backend
 * (`GET main/permission-groups/front-first-tree`) — grupo/subgrupo/ação com
 * os `ids[]` (system_action_param.id REAIS, front+back já unidos por folha)
 * resolvidos server-side. Este componente é só apresentação/tri-state/busca;
 * não deriva nem reagrupa nada — marcar uma folha concede TODOS os ids dela
 * de uma vez (front+back juntos, decisão da spec §4).
 *
 * Busca por tela: casa o texto digitado com o `pathLabel` (breadcrumb
 * "ÁREA > TELA" montado pelo wazir-api a partir do `menu.json`), o rótulo
 * da tela e o rótulo das ações — acento-insensível. Diferença vs. ABCX: o
 * wazir-web não recompõe o `pathLabel` ao vivo a partir do menu (o backend
 * é a fonte única da ordem/nomes do menu — mesmo `menu.json` do front).
 */
@Component({
  selector: 'app-front-first-permission-tree',
  templateUrl: './front-first-permission-tree.component.html',
  styleUrls: ['./front-first-permission-tree.component.sass'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FrontFirstPermissionTreeComponent implements OnChanges {
  @Input() groups: FrontFirstTreeGroupDto[] = [];
  @Input() selectedIds: string[] = [];
  @Input() disabled = false;
  @Input() loading = false;
  /**
   * Rótulo de contexto opcional (ex.: nome do grupo sendo editado) — barra
   * de topo do protótipo. Aceita `null` porque o consumidor típico
   * (`crud-ces`) passa `form.value.name`, cujo tipo de `FormGroup` inclui
   * `null` (campo ainda vazio) — o template já trata `null` como "sem
   * contexto" via `*ngIf="contextLabel"`.
   */
  @Input() contextLabel?: string | null;

  @Output() selectedIdsChange = new EventEmitter<string[]>();

  searchText = '';

  /**
   * Toggle geral "Mostrar mapeamento back" (padrão do protótipo, `.m-controls
   * .bar`) — quando ligado, TODAS as ações exibem o painel de detalhe
   * front/back (ver {@link isActionDetailOpen}), sem precisar abrir uma por
   * uma. Visão de suporte: os ids reais (`frontIds`/`backIds`) já vêm
   * resolvidos no DTO (Fase B) e não eram exibidos em lugar nenhum — só o
   * booleano agregado `hasFront`/`hasBack` (tag "front"/"back").
   */
  showBackMapping = false;

  private selectedSet = new Set<string>();
  private expandedGroups = new Set<string>();
  private expandedSubgroups = new Set<string>();
  /** Ações com o painel de detalhe front/back aberto individualmente (ícone "ⓘ"). */
  private detailOpenActions = new Set<string>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedIds']) {
      this.selectedSet = new Set((this.selectedIds || []).map(String));
    }
    // A árvore SEMPRE nasce fechada (todos os grupos recolhidos), independente
    // de haver seleção — pedido do P.O. O usuário expande via "Expandir tudo"
    // ou clicando no grupo. (Antes: auto-expandia grupos com seleção, o que
    // abria quase tudo em grupos com muitas permissões marcadas.)
  }

  // ─── Ícone / breadcrumb ao vivo ─────────────────────────────────────────

  groupIcon(group: FrontFirstTreeGroupDto): string {
    return frontFirstGroupIcon(group.key);
  }

  /**
   * pathLabel AO VIVO (menu real) quando disponível; senão o do DTO
   * (estático/off-menu). Valor "cru" — separador ` > ` (o mesmo formato de
   * `MenuNavSearchSource`/`INavSearchItem.pathLabel`) — usado para casar com
   * a busca (`visibleSubgroups`) e para qualquer consumidor que precise do
   * dado bruto. Para EXIBIR na tela, usar {@link displayPathLabel} (não
   * mexe aqui — nem no `MenuNavSearchSource` compartilhado — para não
   * afetar a barra de busca do navbar).
   */
  livePathLabel(sub: FrontFirstTreeSubgroupDto): string {
    return sub.pathLabel;
  }

  /**
   * pathLabel formatado para EXIBIÇÃO — troca o separador ASCII ` > ` pelo
   * caractere do protótipo (` › `, U+203A), só na apresentação. Reusa
   * {@link livePathLabel} (mesmo dado); nunca toca `MenuNavSearchSource` nem
   * o `pathLabel` usado pela busca (`visibleSubgroups`), que continuam no
   * formato original.
   */
  displayPathLabel(sub: FrontFirstTreeSubgroupDto): string {
    return this.livePathLabel(sub).split(' > ').join(' › ');
  }

  // ─── Busca por rota/tela (normalize igual ao NavSearchService) ─────────

  // Mesmo algoritmo de NavSearchService.normalize (a barra de pesquisa) —
  // acento-insensível, consistente com a busca por rota do navbar.
  private normalize(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(DIACRITICS_REGEX, '')
      .toLowerCase()
      .trim();
  }

  /** Chave (de expansão) do nó sintético que agrupa os grupos `advancedOnly`. */
  readonly systemOnlyKey = '__fft_system_only__';
  readonly systemOnlyLabel = 'Ações de sistema sem tela';

  /** Grupos de tela/menu (tudo que NÃO é `advancedOnly`), na ordem da API. */
  get mainGroups(): FrontFirstTreeGroupDto[] {
    return this.visibleGroups.filter((g) => !g.advancedOnly);
  }

  /** Grupos `advancedOnly` (catch-all sem tela) visíveis — renderizados no fim, sob um nó recolhido. */
  get systemOnlyGroups(): FrontFirstTreeGroupDto[] {
    return this.visibleGroups.filter((g) => !!g.advancedOnly);
  }

  /** Total de ids do nó "sem tela" (união, sem duplicar; independe da busca). */
  get systemOnlyIdsCount(): number {
    return this.systemOnlyIds.size;
  }

  get systemOnlySelectedCount(): number {
    let count = 0;
    this.systemOnlyIds.forEach((id) => {
      if (this.selectedSet.has(id)) count += 1;
    });
    return count;
  }

  private get systemOnlyIds(): Set<string> {
    const all = new Set<string>();
    (this.groups || []).filter((g) => g.advancedOnly).forEach((g) => g.ids.forEach((id) => all.add(id)));
    return all;
  }

  get visibleGroups(): FrontFirstTreeGroupDto[] {
    const term = this.normalize(this.searchText);
    if (!term) return this.groups || [];
    return (this.groups || []).filter((g) => this.visibleSubgroups(g).length > 0 || this.normalize(g.label).includes(term));
  }

  visibleSubgroups(group: FrontFirstTreeGroupDto): FrontFirstTreeSubgroupDto[] {
    const term = this.normalize(this.searchText);
    if (!term) return group.subgroups;
    return group.subgroups.filter((s) => this.subgroupMatchesSearch(s, term));
  }

  /**
   * Profundidade arbitrária: filhos (sub-telas aninhadas) visíveis DENTRO de
   * uma tela — mesma regra de {@link visibleSubgroups}, um nível abaixo.
   * Sem busca, mostra todos; buscando, só os filhos que casam (o próprio
   * texto/pathLabel/ações OU algum descendente deles, recursivamente) —
   * nunca esconde um descendente que casa só porque o pai/irmão não casou.
   */
  visibleChildren(sub: FrontFirstTreeSubgroupDto): FrontFirstTreeSubgroupDto[] {
    const children = sub.children || [];
    const term = this.normalize(this.searchText);
    if (!term) return children;
    return children.filter((c) => this.subgroupMatchesSearch(c, term));
  }

  /** `true` se o próprio subgrupo (rótulo/caminho/ações) casa com o termo OU
   * QUALQUER descendente (em qualquer profundidade) casa — a busca nunca
   * para na 1ª camada de `children`. */
  private subgroupMatchesSearch(sub: FrontFirstTreeSubgroupDto, term: string): boolean {
    const haystack = this.normalize([this.livePathLabel(sub), sub.label, ...sub.actions.map((a) => a.label)].join(' '));
    if (haystack.includes(term)) return true;
    return (sub.children || []).some((child) => this.subgroupMatchesSearch(child, term));
  }

  get isSearching(): boolean {
    return !!this.searchText.trim();
  }

  clearSearch(): void {
    this.searchText = '';
  }

  // ─── Expandir/recolher tudo (toolbar, fiel ao protótipo) ────────────────

  /** Expande TODOS os grupos e telas de uma vez (botão "Expandir tudo") —
   * em QUALQUER profundidade (aninhamento arbitrário: CADASTRO > PRODUTOS >
   * CATEGORIAS/... também abrem juntos). */
  expandAll(): void {
    this.expandedGroups.add(this.systemOnlyKey);
    (this.groups || []).forEach((g) => {
      this.expandedGroups.add(g.key);
      this.flattenSubgroups(g.subgroups).forEach((s) => this.expandedSubgroups.add(s.key));
    });
    this.cdr.markForCheck();
  }

  /** Recolhe TODOS os grupos e telas de uma vez (botão "Recolher tudo"). */
  collapseAll(): void {
    this.expandedGroups.clear();
    this.expandedSubgroups.clear();
    this.cdr.markForCheck();
  }

  /** Achata uma lista de subgrupos (profundidade arbitrária) numa lista
   * única — usado por operações que precisam varrer TODOS os nós da
   * sub-árvore (expandir tudo, contagem de telas com seleção), nunca só o
   * 1º nível de `children`. */
  private flattenSubgroups(subs: FrontFirstTreeSubgroupDto[]): FrontFirstTreeSubgroupDto[] {
    const out: FrontFirstTreeSubgroupDto[] = [];
    for (const sub of subs) {
      out.push(sub);
      if (sub.children?.length) out.push(...this.flattenSubgroups(sub.children));
    }
    return out;
  }

  // ─── Tri-state: GRUPO ────────────────────────────────────────────────────

  isGroupChecked(group: FrontFirstTreeGroupDto): boolean {
    return group.ids.length > 0 && group.ids.every((id) => this.selectedSet.has(id));
  }

  isGroupIndeterminate(group: FrontFirstTreeGroupDto): boolean {
    if (this.isGroupChecked(group)) return false;
    return group.ids.some((id) => this.selectedSet.has(id));
  }

  groupSelectedCount(group: FrontFirstTreeGroupDto): number {
    return group.ids.filter((id) => this.selectedSet.has(id)).length;
  }

  toggleGroup(group: FrontFirstTreeGroupDto, checked: boolean): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    if (checked) group.ids.forEach((id) => next.add(id));
    else group.ids.forEach((id) => next.delete(id));
    this.applyChange(next);
  }

  isExpanded(groupKey: string): boolean {
    return this.expandedGroups.has(groupKey);
  }

  toggleExpand(groupKey: string): void {
    if (this.expandedGroups.has(groupKey)) this.expandedGroups.delete(groupKey);
    else this.expandedGroups.add(groupKey);
    this.cdr.markForCheck();
  }

  // ─── Tri-state: SUBGRUPO (tela) ──────────────────────────────────────────

  isSubgroupChecked(sub: FrontFirstTreeSubgroupDto): boolean {
    return sub.ids.length > 0 && sub.ids.every((id) => this.selectedSet.has(id));
  }

  isSubgroupIndeterminate(sub: FrontFirstTreeSubgroupDto): boolean {
    if (this.isSubgroupChecked(sub)) return false;
    return sub.ids.some((id) => this.selectedSet.has(id));
  }

  subgroupSelectedCount(sub: FrontFirstTreeSubgroupDto): number {
    return sub.ids.filter((id) => this.selectedSet.has(id)).length;
  }

  /** Marcar a TELA concede front+back dela inteira (decisão da spec §4/§5). */
  toggleSubgroup(sub: FrontFirstTreeSubgroupDto, checked: boolean): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    if (checked) sub.ids.forEach((id) => next.add(id));
    else sub.ids.forEach((id) => next.delete(id));
    this.applyChange(next);
  }

  isSubgroupExpanded(subKey: string): boolean {
    return this.expandedSubgroups.has(subKey);
  }

  toggleSubgroupExpand(subKey: string): void {
    if (this.expandedSubgroups.has(subKey)) this.expandedSubgroups.delete(subKey);
    else this.expandedSubgroups.add(subKey);
    this.cdr.markForCheck();
  }

  // ─── AÇÃO (folha) — front+back concedidos juntos ────────────────────────

  isActionChecked(action: FrontFirstTreeActionDto): boolean {
    return action.ids.length > 0 && action.ids.every((id) => this.selectedSet.has(id));
  }

  isActionIndeterminate(action: FrontFirstTreeActionDto): boolean {
    if (this.isActionChecked(action)) return false;
    return action.ids.some((id) => this.selectedSet.has(id));
  }

  toggleAction(action: FrontFirstTreeActionDto, checked: boolean): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    if (checked) action.ids.forEach((id) => next.add(id));
    else action.ids.forEach((id) => next.delete(id));
    this.applyChange(next);
  }

  // ─── Drill-down front/back por ação (visão de suporte) ──────────────────

  /** Toggle geral (ver {@link showBackMapping}) OU aberto individualmente via "ⓘ". */
  isActionDetailOpen(action: FrontFirstTreeActionDto): boolean {
    return this.showBackMapping || this.detailOpenActions.has(action.key);
  }

  toggleActionDetail(action: FrontFirstTreeActionDto, event: Event): void {
    event.stopPropagation();
    if (this.detailOpenActions.has(action.key)) this.detailOpenActions.delete(action.key);
    else this.detailOpenActions.add(action.key);
    this.cdr.markForCheck();
  }

  toggleBackMapping(): void {
    this.showBackMapping = !this.showBackMapping;
    this.cdr.markForCheck();
  }

  // ─── Estatísticas do cabeçalho ("X permissões selecionadas · N telas") ──

  get totalSelectedCount(): number {
    const all = new Set<string>();
    (this.groups || []).forEach((g) => g.ids.forEach((id) => all.add(id)));
    return [...all].filter((id) => this.selectedSet.has(id)).length;
  }

  get totalIdsCount(): number {
    const all = new Set<string>();
    (this.groups || []).forEach((g) => g.ids.forEach((id) => all.add(id)));
    return all.size;
  }

  /**
   * "N tela(s)" do resumo do cabeçalho — soma RECURSIVAMENTE (profundidade
   * arbitrária): conta só as TELAS-FOLHA (subgrupo sem `children` — um
   * subgrupo-container organizacional, ex.: a pasta "PRODUTOS", não é uma
   * tela por si só, é o agregador das telas reais dentro dele) que têm ao
   * menos 1 id selecionado.
   */
  get screensWithSelectionCount(): number {
    let count = 0;
    (this.groups || []).forEach((g) => {
      this.flattenSubgroups(g.subgroups).forEach((s) => {
        if (!this.hasChildren(s) && this.subgroupSelectedCount(s) > 0) count += 1;
      });
    });
    return count;
  }

  /** `true` quando o subgrupo tem sub-telas aninhadas (profundidade
   * arbitrária) — usado pelo template pra decidir se renderiza o bloco
   * recursivo de `children` além das ações próprias. */
  hasChildren(sub: FrontFirstTreeSubgroupDto): boolean {
    return !!sub.children?.length;
  }

  /** Recuo (padding-left) por profundidade — cresce a cada nível aninhado,
   * fiel ao protótipo (indentação progressiva). */
  subIndent(depth: number): number {
    return 30 + depth * 18;
  }

  actionIndent(depth: number): number {
    return 54 + depth * 18;
  }

  // ─── Utils ────────────────────────────────────────────────────────────

  /**
   * Botão "Limpar" do rodapé (protótipo `.m-foot`) — desmarca TODAS as permissões
   * PRESENTES na árvore (independe do texto da busca; diferente de {@link clearSearch},
   * que só limpa o texto). Preserva os ids que NÃO estão na árvore (ocultos/residuais —
   * ex.: permissões que o ator não possui/não enxerga e que já estavam no destino):
   * mesmo contrato do antigo `clearAllVisible` do seletor legado, nunca apaga o que o
   * usuário não vê.
   */
  clearSelection(): void {
    if (this.disabled) return;
    const inTree = new Set<string>();
    (this.groups || []).forEach((g) => g.ids.forEach((id) => inTree.add(String(id))));
    this.applyChange(new Set([...this.selectedSet].filter((id) => !inTree.has(id))));
  }

  private applyChange(next: Set<string>): void {
    this.selectedSet = next;
    this.selectedIdsChange.emit([...next]);
    this.cdr.markForCheck();
  }

  trackByGroupKey(_i: number, g: FrontFirstTreeGroupDto): string {
    return g.key;
  }

  trackBySubKey(_i: number, s: FrontFirstTreeSubgroupDto): string {
    return s.key;
  }

  trackByActionKey(_i: number, a: FrontFirstTreeActionDto): string {
    return a.key;
  }
}
